import { styled } from '@atom-web/core'
import ChevronLeft from '@atom-web/icons-24/ChevronLeft'
import CloseLine from '@atom-web/icons-24/CloseLine'
import Edit from '@atom-web/icons-24/Edit'
import EditBox from '@atom-web/icons-24/EditBox'
import Global from '@atom-web/icons-24/Global'
import Info from '@atom-web/icons-24/Info'
import { Dialog } from '@atom-web/macros'
import { Box, Button, Column, Row, Text } from '@atom-web/micros'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import { Fragment, useContext, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import OtpInput from 'react-otp-input'
import { useDispatch } from 'react-redux'

import { AppConfig } from '../../../app-config'
import AirportCityDropdown from '../../../components/gcm/AirportCityDropdown'
import CustomerMobile from '../../../components/gcm/components/CustomerMobile'
import CustomerName from '../../../components/gcm/components/CustomerName'
import {
	CHOOSE_LANGUAGE,
	COSTING_INFO,
	DEPARTURE_AIRPORT_CHANGE,
	DEPARTURE_DATE_CHANGED,
	GCM,
	GCM_INDIAN_CITIES,
	GCM_PASSENGER_INFO,
	LOGIN_ERROR,
	NATIONALITY_GLOBAL,
	OTP,
	UPDATE_COSTING,
} from '../../../components/gcm/Constants'
import DepartureDate from '../../../components/gcm/DepartureDate'
import GCMContext from '../../../components/gcm/GCMContext'
import Duration from '../../../components/gcm/macros/Duration'
import RoomsInfo from '../../../components/gcm/macros/RoomsInfo'
import {
	DEPARTURE_CITY,
	DEPARTURE_DATE,
	DURATION,
	ROOM_INFO,
} from '../../../components/gcm/routeConstant'
import getGCMDefaultData from '../../../components/gcm/utils/getGCMDefaultData'
import getIndianCities from '../../../components/gcm/utils/getIndianCities'
import LanguageSelection from '../../../components/LanguageSelection'
import { useDidUpdate } from '../../../hooks/useDidUpdate'
import AppContext from '../../../store/AppContext'
import { SET_USER_DETAILS } from '../../../store/Constants'
import { getLeadSource } from '../../../utils/analytics/leadSourceHelper'
import nationalityCountriesList from '../../../utils/countryListData'
import { decodeHotelRoomConfig } from '../../../utils/decodeHotelRoomConfig'
import { encodeHotelRoomConfig } from '../../../utils/encodeHotelRoomConfig'
import getImgIXUrl from '../../../utils/getImgIXUrl'
import { getPaxCount } from '../../../utils/getPaxCount'
import getSafariDateFormat from '../../../utils/getSafariDateFormat'
import { triggerSimpleAjax } from '../../../utils/http-helper'
import isIxigoPartnersSite from '../../../utils/isIxigoPartnersSite'
import getLocale from '../../../utils/locale-helpers/getLocale'
import get from '../../../utils/lodashUtils/get'
import isEmpty from '../../../utils/lodashUtils/isEmpty'
import {
	EVENT_CHOOSE_LANGUAGE,
	EVENT_GET_OTP_NEW_USER,
	EVENT_MOB_NO_ENTERED_FOR_OTP,
	EVENT_RE_SEND_OTP,
	trackEvent,
} from '../../../utils/ML/EventsTracker'
import triggerOTP from '../../auth/actions/triggerOTP'
import triggerVerifyOTP from '../../auth/actions/triggerVerifyOTP'
import isValidMobileNumber from '../../auth/utils/isValidMobileNumber'
import triggerPrecostedMaldivesDeal from '../../maldives/pre-costed/actions/triggerPrecostedMaldivesDeal'
import triggerTourPackageViewDeal from '../../tour-packages/actions/triggerTourPackageViewDeal'
import triggerPackageGetCost from '../actions/triggerPackageGetCost'

const GcmModal = ({ itineraryInfo, regionName, onSuccess, onAuthSuccess }) => {
	const router = useRouter()

	const [visible, setVisible] = useState(false)
	const [modalState, setModalState] = useState(null)
	const [ctaLoading, setCtaLoading] = useState(false)
	const [otp, setOtp] = useState('')
	const [resendDisable, setResendDisable] = useState(true)
	const [counter, setCounter] = useState(20)
	const [otpError, setOtpError] = useState({
		error: false,
		status: '',
		message: '',
	})
	// eslint-disable-next-line no-unused-vars
	const [passengerType, setPassengerType] = useState('')
	const [inputLoading, setInputLoading] = useState(false)
	const [showName, setShowName] = useState(false)
	const [disableGCM, setDisableGCM] = useState(false)
	const [showAlert, setShowAlert] = useState(false)
	const [isChecked, setChecked] = useState({ id: '', value: false })
	const [{ userDetails }, appDispatch] = useContext(AppContext)
	const isIxigoPartnersSiteValue = isIxigoPartnersSite()

	const reduxDispatch = useDispatch()
	const [state, dispatch] = useContext(GCMContext)

	const locale = getLocale({ router })

	// Modal data
	const modalName = useMemo(() => router.query?.m, [router])
	const regionCode = useMemo(() => router.query?.rc, [router])
	const destination = useMemo(() => router.query?.rn, [router])
	const isLoggedIn = useMemo(() => get(userDetails, 'loggedIn'), [userDetails])
	const price = useMemo(() => router.query?.price, [router])
	const isMle = useMemo(() => router.query?.sb === '1', [router])
	const isSbPackages = useMemo(() => router.query?.sbPackages === '1', [router])
	const date = useMemo(() => router.query?.date, [router])
	const nights = useMemo(() => router.query?.duration, [router])
	const airportCode = useMemo(() => router.query?.arrivalAirportCode, [router])
	const airportName = useMemo(() => router.query?.arrivalAirportName, [router])
	const travellers = useMemo(() => router.query?.travellers, [router])
	const resortCode = useMemo(() => router.query.resortCode, [router])
	const mleLanding = useMemo(() => modalName === 'mle-landing', [modalName])
	const preCosted = useMemo(() => router.query.preCosted === '1', [router])
	const isGlobalUser = useMemo(() => Boolean(locale), [locale])

	const {
		flightsBookedByUserAlready = false,
		travelType,
		cities = [],
		departureAirport,
		arrivalAirport,
		departureAirportText,
		departureDate,
		duration,
		hotelGuestRoomConfigurations,
		nationality,
		passenger_details,
		loginError,
		otpResponseObj,
	} = state

	useEffect(() => {
		if (isMle || isSbPackages) {
			dispatch({
				type: DEPARTURE_DATE_CHANGED,
				payload: {
					departureDate: dayjs(date).format('DD/MMM/YYYY'),
					duration: nights || 4,
				},
			})
			dispatch({
				type: DEPARTURE_AIRPORT_CHANGE,
				payload: {
					departureAirport: airportCode,
					departureAirportText: airportName,
				},
			})
			dispatch({
				type: UPDATE_COSTING,
				payload: {
					hotelGuestRoomConfigurations: decodeHotelRoomConfig(travellers),
				},
			})
		} else {
			dispatch({
				type: DEPARTURE_DATE_CHANGED,
				payload: {
					departureDate: dayjs().add(7, 'day').format('DD/MMM/YYYY'),
					duration: nights || 4,
				},
			})
			dispatch({
				type: UPDATE_COSTING,
				payload: {
					hotelGuestRoomConfigurations: decodeHotelRoomConfig('a2'),
				},
			})
			if (regionCode) {
				triggerSimpleAjax({
					locale,
					url: `${AppConfig.api_url}region/${regionCode}/info`,
					method: 'GET',
					onSuccess: ({ data }) => {
						if (data?.nearestAirport?.airportCode) {
							dispatch({
								type: DEPARTURE_AIRPORT_CHANGE,
								payload: {
									departureAirport: data?.nearestAirport?.airportCode,
									departureAirportText: data?.nearestAirport?.cityName,
								},
							})
						} else {
							// Fallback: Set Chennai as Departure city
							dispatch({
								type: DEPARTURE_AIRPORT_CHANGE,
								payload: {
									departureAirport: 'MAA',
									departureAirportText: 'Chennai',
								},
							})
						}
						dispatch({
							type: DEPARTURE_DATE_CHANGED,
							payload: {
								departureDate: dayjs().add(7, 'day').format('DD/MMM/YYYY'),
							},
						})
					},
					onFailure: () => {
						// Fallback: Set Chennai as Departure city
						dispatch({
							type: DEPARTURE_AIRPORT_CHANGE,
							payload: {
								departureAirport: 'MAA',
								departureAirportText: 'Chennai',
							},
						})
						dispatch({
							type: DEPARTURE_DATE_CHANGED,
							payload: {
								departureDate: dayjs().add(7, 'day').format('DD/MMM/YYYY'),
							},
						})
					},
				})
			}
		}
	}, [
		dispatch,
		locale,
		regionCode,
		isMle,
		isSbPackages,
		nights,
		date,
		airportCode,
		airportName,
		travellers,
	])

	useEffect(() => {
		if (!cities?.length) {
			getIndianCities(response => {
				dispatch({
					type: GCM_INDIAN_CITIES,
					payload: response,
				})
			})
		}
		if (!visible && modalName === 'gcm') {
			setVisible(true)
			if (isIxigoPartnersSiteValue || isLoggedIn) {
				setModalState(DEPARTURE_CITY)
			} else {
				if (modalName === 'gcm') {
					setModalState(GCM)
				}
			}
		} else if (!visible && mleLanding) {
			setVisible(true)
			setModalState(DEPARTURE_CITY)
		}
	}, [modalName])

	useDidUpdate(() => {
		if (!visible) {
			dispatch({
				type: UPDATE_COSTING,
				payload: {
					passenger_details: {
						mobileNumber: '',
						name: '',
					},
				},
			})
			setShowName(false)
			setInputLoading(false)
			if (isMle) {
				const path = router.asPath?.split('?')[0]
				const queryParams = { ...router.query }
				delete queryParams.m

				router.replace(
					{
						pathname: path,
						query: queryParams,
					},
					null,
					{ shallow: true, scroll: false },
				)
			} else {
				const queryParams = { ...router.query }
				delete queryParams.m
				const path = router.asPath.split('?')[0]
				router.replace(`${path}`, null, { shallow: true, scroll: false })
			}
		}
	}, [visible])

	useEffect(() => {
		if (modalState === OTP) {
			triggerCounter()
			setOtp('')
		} else {
			resetCounter()
		}
	}, [modalState])

	useDidUpdate(() => {
		if (
			!isEmpty(passenger_details?.mobileNumber) &&
			isValidMobileNumber(
				passenger_details?.countryPhoneCode + passenger_details?.mobileNumber,
			)
		) {
			setInputLoading(true)
		}
	}, [passenger_details?.countryPhoneCode, passenger_details?.mobileNumber])

	useEffect(() => {
		let result = false
		if (
			isEmpty(passenger_details?.mobileNumber) ||
			!isValidMobileNumber(
				passenger_details?.countryPhoneCode + passenger_details?.mobileNumber,
			)
		) {
			result = true
		}
		if (showName) {
			if (
				isEmpty(passenger_details?.name) &&
				passenger_details?.countryPhoneCode === '+91'
			) {
				result = true
			}
		}
		setDisableGCM(result)
	}, [passenger_details, showName, modalState])

	useEffect(() => {
		if (inputLoading) {
			triggerMobileOTP()
		}
	}, [inputLoading])

	const onClose = () => {
		setCtaLoading(false)
		setVisible(false)
		dispatch({
			type: COSTING_INFO,
			payload: getGCMDefaultData(itineraryInfo, regionName),
		})
		if (loginError)
			dispatch({
				type: LOGIN_ERROR,
				payload: {
					message: null,
				},
			})
	}

	const triggerCounter = () => {
		let interval
		let count = 20
		setResendDisable(false)
		interval = setInterval(() => {
			if (count !== 0) {
				count -= 1
				setCounter(count)
			} else {
				setCounter(20)
				setResendDisable(true)
				clearInterval(interval)
			}
		}, 1000)
	}

	const resetCounter = () => {
		setCounter(20)
	}

	const triggerGetCost = ({
		userDetails,
		paxType,
		paxValue,
		loginSuccess = false,
	}) => {
		let updatedTravellers

		if (paxType === 'SOLO') {
			const updatedConfigurations = hotelGuestRoomConfigurations.map(room => ({
				...room,
				adultCount: 1,
				childAges: [],
			}))

			updatedTravellers = encodeHotelRoomConfig(updatedConfigurations)
		} else if (paxType === 'COUPLE') {
			const updatedConfigurations = hotelGuestRoomConfigurations.map(room => ({
				...room,
				adultCount: 2,
				childAges: [],
			}))

			updatedTravellers = encodeHotelRoomConfig(updatedConfigurations)
		} else {
			updatedTravellers =
				encodeHotelRoomConfig(hotelGuestRoomConfigurations) || 'a2'
		}

		setCtaLoading(true)
		if (preCosted) {
			triggerPrecostedMaldivesDeal({
				lastLocation: {
					pathname: router.asPath,
				},
				router,
				passengerConfiguration: hotelGuestRoomConfigurations,
				passengerDetail: passenger_details,
				locale,
				departureDate,
				nights: duration,
				departureAirportCode: departureAirport,
				appDispatch,
				gcmDispatch: dispatch,
				userDetails,
				setCtaLoading,
				setVisible,
				setModalState,
			})
		} else if (isMle) {
			router.query.date = dayjs(restructuredDepDate).format('YYYY-MM-DD')
			router.query.duration = duration

			router.query.travellers = updatedTravellers
			router.query.arrivalAirportCode = departureAirport
			router.query.from = departureAirport
			router.query.arrivalAirportName = departureAirportText
			delete router.query.m
			const routerPath = `${router.pathname}${
				queryString.stringify(router.query)
					? `?${queryString.stringify(router.query)}`
					: ''
			}`
			reduxDispatch({
				type: SET_USER_DETAILS,
				payload: { ...userDetails, isFetched: true },
			})
			onClose && onClose()
			if (loginSuccess) {
				if (resortCode) {
					onAuthSuccess && onAuthSuccess()
				} else {
					router
						.push(routerPath, null, { shallow: true })
						.then(() => router.reload())
				}
			} else {
				router.push(routerPath, null, { shallow: true })
				onSuccess && onSuccess()
			}
		} else if (mleLanding) {
			const obj = {
				date: dayjs(restructuredDepDate).format('YYYY-MM-DD'),
				from: departureAirport,
				travellers:
					paxType == 'SOLO'
						? encodeHotelRoomConfig(paxValue)
						: encodeHotelRoomConfig(hotelGuestRoomConfigurations),
				duration,
				nationality: nationality || 'IN',
				arrivalAirportCode: departureAirport,
				arrivalAirportName: departureAirportText,
			}
			if (price) obj.price = price
			router.push(
				`/maldives-tour-packages/listing?${queryString.stringify(obj)}`,
				null,
				{ shallow: true },
			)
		} else if (resortCode || isSbPackages) {
			triggerTourPackageViewDeal({
				lastLocation: {
					pathname: router.asPath,
				},
				router,
				hotelGuestRoomConfigurations,
				passengerDetail: passenger_details,
				locale,
				appDispatch,
				gcmDispatch: dispatch,
				userDetails,
				costingConfig: {
					passengerConfiguration: hotelGuestRoomConfigurations,
					departureAirportCode: departureAirport,
					arrivalAirportCode: arrivalAirport,
					departureDate: dayjs(restructuredDepDate).format('YYYY-MM-DD'),
					travelType,
					nationality: nationality ? nationality : 'IN',
					nights: duration,
				},
			})
		} else {
			triggerPackageGetCost({
				costingConfig: {
					hotelGuestRoomConfigurations,
					departureAirport,
					arrivalAirport,
					departureDate: dayjs(restructuredDepDate).format('DD/MMM/YYYY'),
					travelType,
					nationality: nationality ? nationality : 'IN',
				},
				flightsBookedByUserAlready,
				lastLocation: {
					pathname: router.asPath,
				},
				router,
				query: router.query,
				passengerDetail: passenger_details,
				locale,
				appDispatch,
				gcmDispatch: dispatch,
				userDetails,
				setVisible,
				setModalState,
			})
		}
	}

	const triggerMobileOTP = (isResend = false) => {
		setOtp('')
		setOtpError({
			error: false,
			status: '',
			message: '',
		})

		const eventProps = {
			is_logged_in: false,
			leadSource: getLeadSource({
				router,
				lastLocation: {
					pathname: router.asPath,
				},
			}),
			ipAddress: sessionStorage.getItem('ipaddress') || '',
			regionCode,
			mobileNumber: passenger_details.mobileNumber,
			countryPhoneCode: passenger_details.countryPhoneCode,
		}
		if (isResend) {
			trackEvent(EVENT_RE_SEND_OTP, eventProps)
		} else if (passenger_details.name) {
			trackEvent(EVENT_GET_OTP_NEW_USER, eventProps)
		} else {
			trackEvent(EVENT_MOB_NO_ENTERED_FOR_OTP, eventProps)
		}

		const reqObj = {
			countryPhoneCode: passenger_details.countryPhoneCode,
			mobileNumber: passenger_details.mobileNumber,
			name: showName ? passenger_details.name : undefined,
		}

		triggerOTP({ reqObj }).then(async res => {
			setInputLoading(false)

			if (!res.data.name) {
				if (passenger_details.countryPhoneCode === '+91') {
					setShowName(true)
				}
			} else {
				setModalState(OTP)
			}
			dispatch({
				type: UPDATE_COSTING,
				payload: {
					otpSuccess: true,
					otpResponseObj: res.data,
					name: '',
				},
			})
		})
	}

	const triggerVerify = () => {
		const reqObj = {
			...otpResponseObj,
			mobileNumber: passenger_details?.mobileNumber,
			name: passenger_details?.name || otpResponseObj?.name,
			userName: passenger_details?.name || otpResponseObj?.userName,
			preferredLanguage:
				passenger_details?.language || otpResponseObj?.preferredLanguage,
			otp,
			canSendWhatsAppMessages: true,
			leadSource: getLeadSource({
				router,
				lastLocation: {
					pathname: router.asPath,
				},
				repeatGCM: isChecked?.value,
			}),
		}
		setCtaLoading(true)
		triggerVerifyOTP({
			reqObj,
			router,
			lastLocation: {
				pathname: router.asPath,
			},
			dispatch,
		})
			.then(res => {
				const { status, user, message } = res

				if (status === 'VERIFIED') {
					triggerGetCost({ userDetails: user, loginSuccess: true })
				} else {
					setCtaLoading(false)
					setOtpError({
						error: true,
						status: status,
						message: message,
					})
				}
			})
			.catch(({ status, message }) => {
				setCtaLoading(false)
				setOtpError({
					error: true,
					status: status,
					message: message,
				})
			})
	}

	const otpDisabled = useMemo(() => otp.length === 6, [otp])
	const isLocalUser = useMemo(
		() => passenger_details?.countryPhoneCode === '+91',
		[passenger_details?.countryPhoneCode],
	)

	const handlePaidLandingFlow = () => {
		setModalState(isSbPackages ? DEPARTURE_DATE : DEPARTURE_CITY)
	}

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden) {
				setChecked({ id: '', value: false })
			}
		}

		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [])

	const onBack = () => {
		switch (modalState) {
			case DEPARTURE_CITY:
				setModalState(GCM)
				break
			case DEPARTURE_DATE:
				setModalState(isSbPackages ? GCM : DEPARTURE_CITY)
				break
			case DURATION:
				setModalState(DEPARTURE_DATE)
				break
			case ROOM_INFO:
				setModalState(DEPARTURE_DATE)
				break
			default:
				setModalState(GCM)
				setInputLoading(false)
				setShowName(false)
				break
		}
	}

	// In page modals
	let modalContent = null
	let onSubmit = () => false
	let showHeader = false
	let hideBack = false
	let headerTitle = ''

	const restructuredDepDate = useMemo(() => {
		const seperator = departureDate?.includes('/') ? '/' : '-'
		return getSafariDateFormat({
			date: departureDate,
			format: `dd${seperator}MM${seperator}yyyy`,
			seperator,
		})
	}, [departureDate, visible, date])

	const onChooseLanguage = language => {
		trackEvent(EVENT_CHOOSE_LANGUAGE, {
			language,
		})
		dispatch({
			type: GCM_PASSENGER_INFO,
			payload: {
				language,
			},
		})

		setModalState(OTP)
	}

	const globalUserSignUpError = useMemo(
		() =>
			passenger_details?.countryPhoneCode &&
			passenger_details?.countryPhoneCode !== '+91' &&
			isValidMobileNumber(
				passenger_details?.countryPhoneCode + passenger_details?.mobileNumber,
			) &&
			!otpResponseObj?.name,
		[passenger_details, otpResponseObj],
	)

	//components goes here
	const StyledImage = styled('img')

	switch (modalState) {
		//common components goes here
		case DEPARTURE_CITY:
			showHeader = true
			headerTitle = 'Choose your departure city'
			hideBack = isIxigoPartnersSiteValue || mleLanding
			modalContent = (
				<AirportCityDropdown
					listData={cities}
					isLoggedIn={isLoggedIn}
					callBackAction={() => {
						setModalState(DEPARTURE_DATE)
					}}
				/>
			)
			break
		case NATIONALITY_GLOBAL:
			showHeader = true
			headerTitle = 'Choose your nationality'
			modalContent = (
				<AirportCityDropdown
					isLoggedIn={isLoggedIn}
					listData={nationalityCountriesList}
					callBackAction={() =>
						setModalState(isLoggedIn ? ROOM_INFO : DEPARTURE_DATE)
					}
				/>
			)
			break
		case DEPARTURE_DATE:
			showHeader = true
			headerTitle = 'Choose your date'
			modalContent = (
				<DepartureDate
					isLoggedIn={isLoggedIn}
					onChange={() => {
						if (isMle || mleLanding || preCosted || isSbPackages) {
							setModalState(DURATION)
						} else {
							setModalState(ROOM_INFO)
						}
					}}
					defaultDate={modalName === 'pl-gcm' ? 6 : 2}
				/>
			)
			break
		case DURATION:
			showHeader = true
			headerTitle = 'Choose number of nights'
			modalContent = (
				<Duration
					isLoggedIn={isLoggedIn}
					onChange={duration => {
						dispatch({
							type: UPDATE_COSTING,
							payload: {
								duration,
							},
						})
						setModalState(ROOM_INFO)
					}}
				/>
			)
			break
		case ROOM_INFO:
			showHeader = true
			headerTitle = 'How to configure your rooms?'
			onSubmit = data => {
				if (isLoggedIn) {
					let paxValue = data?.t
					let paxType = data?.paxType
					setCtaLoading(true)
					triggerGetCost({ userDetails, paxValue, paxType })
				} else {
					setModalState(GCM)
				}
			}

			modalContent = (
				<RoomsInfo
					showRoomConfig={false}
					isLoggedIn={isLoggedIn}
					ctaText={
						modalName === 'journey'
							? 'Continue'
							: modalName === 'sb-gcm'
							? 'Update cost'
							: modalName === 'mle-landing'
							? 'Search Packages'
							: isLoggedIn
							? 'View Trip Cost'
							: 'Continue'
					}
					loading={ctaLoading}
					onSumbit={onSubmit}
					onChange={roomConfig => {
						dispatch({
							type: UPDATE_COSTING,
							payload: {
								hotelGuestRoomConfigurations: roomConfig,
							},
						})
					}}
					setPassengerType={setPassengerType}
				/>
			)
			break

		//pages goes here
		case GCM:
			modalContent = (
				<Column
					css={{
						width: '100%',
						height: '100%',
						marginTop: 0,
						gap: '16px',
						padding: '0px 20px',
						'@lg': {
							marginTop: -24,
						},
					}}
				>
					<Column
						css={{
							paddingTop: '16px',

							'@smMax': {
								paddingBottom: '24px',
							},
						}}
					>
						<Text
							css={{
								fontSize: '24px',
								color: '$grey12',
								fontWeight: '$bold',
								lineHeight: '27px',
								letterSpacing: '0.2px',

								'@lg': {
									paddingTop: '56px',
									lineHeight: '32px',
								},

								'@smMax': {
									fontSize: '22px',
								},
							}}
						>
							{!showName
								? 'Enter mobile number to personalise your trip '
								: 'And personalisation starts with your name'}
						</Text>

						<Row
							css={{
								padding: '16px 0',
								marginTop: '$4',
								marginBottom: '18px',
								borderTop: '1px solid $s_custom52',
								borderBottom: '1px solid $s_custom52',
								gap: '12px',

								'@lg': {
									width: '100%',
									padding: '16px 0',
									alignItems: 'center',
								},
							}}
						>
							<Box
								css={{
									width: '48px',
									height: '48px',
									borderRadius: '$round',
									overflow: 'hidden',
								}}
							>
								<StyledImage
									src={getImgIXUrl(
										'https://oceanjar-new.s3.ap-south-1.amazonaws.com/images/web_app/gcm/sample-image-gcm.png',
									)}
									css={{
										width: '100%',
										height: '100%',
										objectFit: 'cover',
									}}
								/>
							</Box>

							<Column>
								<Text
									css={{
										fontSize: '$2',
										fontWeight: '$bold',
										color: '$grey12',
										lineHeight: '$2',
									}}
								>
									Your trip to {destination}
								</Text>
								<Row
									css={{ gap: '$1', cursor: 'pointer' }}
									onClick={handlePaidLandingFlow}
								>
									<Text
										css={{
											fontSize: '$2',
											lineHeight: '$2',
											color: '$primary07',
											textDecoration: 'underline',
											fontWeight: 'bold',
										}}
									>
										From {dayjs(restructuredDepDate).format('MMM DD')}{' '}
										{isMle || isSbPackages ? ` - ${duration} nights` : ''} -{' '}
										{getPaxCount(hotelGuestRoomConfigurations, true)} pax -{' '}
										{departureAirport === '$$$'
											? 'No Flights'
											: departureAirport}
									</Text>
									<Box
										css={{
											backgroundColor: '$primary01',
											padding: '$1',
											borderRadius: '100%',
											display: 'flex',
										}}
									>
										<Edit
											css={{
												fill: '$primary07',
												width: '13px',
												height: '13px',
											}}
										/>
									</Box>
								</Row>
							</Column>
						</Row>
						{!showName || !isLocalUser
							? !isGlobalUser && (
									<CustomerMobile
										labelText={'Enter Mobile Number'}
										isLoggedIn={isLoggedIn}
										itineraryInfo={itineraryInfo}
										context={GCMContext}
										contextKey="passenger_details"
										disabled={inputLoading || (showName && isLocalUser)}
										mobileNumberProps={{
											isLoading: inputLoading,
										}}
									/>
							  )
							: globalUserSignUpError && (
									<Text
										css={{
											fontSize: '12px',
											lineHeight: '16px',
											letterSpacing: '0.18px',
											color: '$red06',
											paddingTop: '$4',
										}}
									>
										Need email & name to register for this country code. Please
										click at the top{' '}
										<Global
											css={{
												fill: '$red06',
											}}
										/>{' '}
										of pickyourtrail.com to change country.
									</Text>
							  )}

						{showName && isLocalUser ? (
							<Row
								align="center"
								css={{
									height: '53px',
									width: '100%',
									borderRadius: '$3',
									backgroundColor: '$custom39',
									paddingLeft: '24px',
								}}
								gap="1"
							>
								<Text
									css={{
										fontSize: '15px',
										lineHeight: '18.86px',
										color: '$grey08',
									}}
								>
									Your Mobile number :
								</Text>
								<Text
									css={{
										fontSize: '15px',
										lineHeight: '18.86px',
										color: '$grey12',
									}}
								>
									{'  ' + passenger_details?.countryPhoneCode}{' '}
									{passenger_details?.mobileNumber}
								</Text>
								<EditBox
									css={{
										width: '18px',
										height: '18px',
										paddingLeft: '$1',
										fill: '$grey12',
										cursor: 'pointer',
									}}
									onClick={() => {
										setShowName(false)
									}}
								/>
							</Row>
						) : (
							globalUserSignUpError && (
								<Text
									css={{
										fontSize: '12px',
										lineHeight: '16px',
										letterSpacing: '0.18px',
										color: '$red06',
										paddingTop: '$4',
									}}
								>
									Need email & name to register for this country code. Please
									click at the top{' '}
									<Global
										css={{
											fill: '$red06',
										}}
									/>{' '}
									of pickyourtrail.com to change country.
								</Text>
							)
						)}
						{showName && isLocalUser && (
							<Box
								css={{
									marginTop: '$5',
								}}
							>
								<CustomerName
									labelText={'Enter your name'}
									isLoggedIn={isLoggedIn}
									itineraryInfo={itineraryInfo}
									context={GCMContext}
									contextKey="passenger_details"
									inputProps={{
										css: {
											backgroundColor: '$white',
										},
										autoFocus: true,
										pattern: '^[a-zA-Z ]*$',
									}}
								/>
							</Box>
						)}
						<Button
							primary
							disabled={disableGCM || globalUserSignUpError || ctaLoading}
							css={{
								height: '48px',
								fontSize: '15px',
								fontWeight: 600,
								cursor: disableGCM ? 'not-allowed' : 'pointer',
								borderRadius: '$4',
								marginTop: '$6',
							}}
							onClick={() => {
								if (otpResponseObj?.name) {
									triggerMobileOTP()
									setOtp('')
									setModalState(
										otpResponseObj?.preferredLanguage ? OTP : CHOOSE_LANGUAGE,
									)
								} else {
									if (isLocalUser && !passenger_details.name) {
										setShowName(true)
									} else {
										triggerMobileOTP()
										if (showName) {
											setOtp('')
											setModalState(
												otpResponseObj?.preferredLanguage
													? OTP
													: CHOOSE_LANGUAGE,
											)
										}
									}
								}
							}}
						>
							Get OTP
						</Button>
					</Column>
				</Column>
			)
			break
		case CHOOSE_LANGUAGE:
			modalContent = (
				<Column
					css={{
						width: '100%',
						gap: '24px',
						'@lg': {
							height: '100%',
							padding: '32px',
						},
						'@smMax': {
							marginTop: '24px',
							paddingLeft: '$4',
							paddingRight: '$4',
						},
					}}
				>
					<Row justify="between">
						<Column
							css={{
								alignItems: 'center',
								'@lg': { alignItems: 'flex-start' },
							}}
						>
							<Text
								css={{
									fontSize: '13px',
									lineHeight: '20px',
									color: '#036D4A',
									fontWeight: '$extrabold',
									textTransform: 'uppercase',
								}}
							>
								Before entering OTP...
							</Text>
							<Text
								css={{
									fontSize: '24px',
									lineHeight: '30px',
									color: '$black',
									fontWeight: '$semibold',
									textAlign: 'center',
									'@lg': {
										textAlign: 'left',
									},
								}}
							>
								Choose the language you're comfortable talking in.
							</Text>
						</Column>
					</Row>

					<LanguageSelection onChange={onChooseLanguage} />
				</Column>
			)
			break
		case OTP:
			modalContent = (
				<Box
					css={{
						width: '100%',
						'@lg': {
							height: '100%',
							padding: '20px',
						},
						'@smMax': {
							marginTop: '24px',
							paddingLeft: '$4',
							paddingRight: '$4',
						},
					}}
				>
					<Column css={{ height: '100%', width: '100%' }}>
						<Text
							css={{
								fontSize: '24px',
								lineHeight: '30px',
								color: '$black',
								fontWeight: '$semibold',
								paddingTop: '$3',
								'@lg': {
									paddingTop: '24px',
								},
							}}
						>
							Enter OTP
						</Text>

						<Text
							css={{
								fontSize: '$3',
								lineHeight: '$2',
								color: '$grey08',
								fontWeight: '$normal',
								paddingTop: '$1',
								'@lg': {
									paddingTop: '12px',
								},
							}}
						>
							{isLocalUser
								? 'OTP has been sent to your Mobile number'
								: 'OTP has been sent to your registered Email'}{' '}
						</Text>
						{isLocalUser && (
							<Row
								gap="1"
								align="center"
								css={{
									padding: '$1',
								}}
							>
								<Text
									css={{
										fontSize: '$3',
										lineHeight: '$2',
										color: '$black',
										fontWeight: '$normal',
									}}
								>
									{passenger_details.countryPhoneCode}{' '}
									{passenger_details.mobileNumber}
								</Text>
								<EditBox
									css={{
										width: '$4',
										height: '$4',
										paddingLeft: '$1',
										fill: '$grey12',
										cursor: 'pointer',
									}}
									onClick={() => {
										setShowName(false)
										const interval_id = window.setInterval(function () {},
										Number.MAX_SAFE_INTEGER)
										for (let i = 1; i < interval_id; i++) {
											window.clearInterval(i)
										}
										resetCounter()
										setModalState(GCM)
										setInputLoading(false)
									}}
								/>
							</Row>
						)}

						<Column
							css={{
								gap: '16px',
								paddingTop: '24px',
							}}
						>
							<Row css={{ width: '100%' }}>
								<OtpInput
									value={otp}
									onChange={e => setOtp(e)}
									numInputs={6}
									isInputNum
									shouldAutoFocus
									inputStyle={{
										minHeight: '46px',
										width: '42px',
										backgroundColor: '#F9F9F9',
										borderRadius: 8,
										textAlign: 'center',
										border: otpError.error
											? '1px solid #D73C54'
											: '1px solid #D8D8D8',
										fontSize: '20px',
										fontWeight: 600,
									}}
									containerStyle={{
										width: '100%',
										display: 'flex',
										justifyContent: 'space-between',
									}}
									focusStyle={{
										outline: 'none',
										border: isIxigoPartnersSiteValue
											? '1px solid #FF8C68'
											: '1px solid #09B367',
										borderRadius: '8px',
									}}
								/>
							</Row>
							<Row justify={otpError.error ? 'between' : 'end'}>
								{otpError.error && (
									<Row gap="1">
										<Info
											css={{
												fill: '$red06',
											}}
										/>
										<Text
											css={{
												fontSize: '13px',
												color: '$red06',
												lineHeight: '15px',
											}}
										>
											{otpError.status === 'VERIFICATION_FAILED'
												? 'Wrong OTP Entered!'
												: otpError.message || 'Wrong OTP Entered!'}
										</Text>
									</Row>
								)}
								{resendDisable ? (
									<Text
										css={{
											fontSize: '13px',
											fontWeight: '$bold',
											color: '$primary06',
											lineHeight: '$1',
											textAlign: 'end',
											cursor: 'pointer',
											textDecorationLine: 'underline',
											textDecorationThickness: '1px',
										}}
										onClick={() => triggerMobileOTP(true)}
									>
										Resend OTP
									</Text>
								) : (
									<Text
										css={{
											fontSize: '13px',
											fontWeight: '$bold',
											color: '$grey09',
											lineHeight: '$1',
											textAlign: 'end',
											cursor: 'not-allowed',
										}}
									>
										Resend OTP in {counter}
									</Text>
								)}
							</Row>
						</Column>
						<Column
							css={{
								flex: 1,
								justifyContent: 'end',
								'@lg': {
									justifyContent: 'start',
								},
								'@smMax': {
									paddingTop: '40px',
								},
							}}
						>
							<Text
								css={{
									fontSize: '13px',
									lineHeight: '$2',
									color: '$custom35',
									fontWeight: '$normal',
									paddingTop: '12px',
									letterSpacing: '0.2px',
									textAlign: 'start',
									'@lg': {
										paddingTop: '24px',
									},
								}}
							>
								By clicking on continue, I agree to receive important
								information through Whatsapp and SMS.
							</Text>
							<Button
								primary
								onClick={triggerVerify}
								disabled={!otpDisabled || ctaLoading}
								css={{
									minHeight: '48px',
									color: '$white',
									fontSize: '15px',
									fontWeight: '$bold',
									marginTop: '24px',
									borderRadius: '$4',
									'@lg': {
										height: '52px',
										marginTop: '10px',
									},
								}}
							>
								Continue
							</Button>
						</Column>
					</Column>
				</Box>
			)
			break
		default:
			null
	}

	const rightSideContent = (
		<Fragment>
			<Column
				css={{
					flex: 1,
					//paddingTop: '24px',
					overflow:
						modalState === DEPARTURE_DATE || modalState === DEPARTURE_CITY
							? 'scroll'
							: 'unset',
					width: '100%',

					'@lg': {
						height: '100%',
						width: '50%',
					},

					'@smMax': {
						flex: 1,
						paddingBottom: '24px',
						// position: modalState === GCM ? 'fixed' : 'flex',
						// bottom:0
					},
				}}
			>
				{showHeader && (
					<Row
						align="center"
						justify="center"
						css={{
							position: modalState === DEPARTURE_DATE ? 'fixed' : 'relative',
							minHeight: modalState === DEPARTURE_DATE ? '48px' : '72px',
							top: modalState === DEPARTURE_DATE ? '24px' : undefined,
							width: modalState === DEPARTURE_DATE ? '50%' : '100%',
							boxShadow: 'rgb(0 0 0 / 6%) 0px 2px 7px',
							textAlign: 'center',
							marginTop: '-24px',
							paddingTop: modalState !== DEPARTURE_DATE ? '24px' : 0,
							backgroundColor:
								modalState === DEPARTURE_DATE ? '$white' : 'transparent',
							borderTopRightRadius:
								modalState === DEPARTURE_DATE ? 12 : undefined,
							'@smMax': {
								width: '100%',
								borderTopRightRadius: 0,
							},
						}}
					>
						{!hideBack && (
							<Box
								css={{
									position: 'absolute',
									left: '$4',
								}}
							>
								<ChevronLeft
									fill="#000000"
									onClick={onBack}
									css={{
										height: 20,
										width: 20,
										transform: 'rotate(360deg)',
										cursor: 'pointer',
									}}
								/>
							</Box>
						)}
						<Text
							css={{
								fontSize: '20px',
								fontFamily: 'Source Sans Pro',
								color: 'rgb(51, 51, 51)',
							}}
						>
							{headerTitle}
						</Text>
					</Row>
				)}
				{modalContent}
			</Column>
		</Fragment>
	)

	return (
		<Dialog.Root
			open={visible}
			onOpenChange={() => {
				if (isMobile && modalState === OTP) {
					if (showAlert) {
						setShowAlert(!showAlert)
					} else if (modalState === GCM) {
						if (showName) {
							setShowAlert(true)
						}
					} else if (modalState === OTP) {
						setShowAlert(true)
					}
				}
			}}
			defaultOpen
		>
			<Dialog.Content
				css={{
					margin: 0,
					padding: 0,
					zIndex: 1050,
					// '& > button': {
					// 	display: 'none',
					// },

					'@lg': {
						display: 'flex',
						height: showAlert ? '427px' : '560px',
						width: '688px',
						minHeight: showAlert ? '427px' : '560px',
						minWidth: '688px',
						borderRadius: '$3',
					},
					'@smMax': {
						all: 'revert',
						backgroundColor: '$white',
						position: 'fixed',
						top: 0,
						bottom: 0,
						left: 0,
						right: 0,
						outline: 0,
						overflow: modalState === GCM ? 'scroll' : 'inherit',
					},
				}}
				closeIcon={
					showAlert ? (
						<></>
					) : (
						<CloseLine
							width={22}
							height={22}
							css={{
								fill:
									modalState === GCM || modalState === CHOOSE_LANGUAGE
										? '$white'
										: '$grey12',
								'@lg': {
									fill: '$grey12',
								},
							}}
							onClick={() => {
								setChecked({ id: '', value: false })
								if (showAlert) {
									setShowAlert(!showAlert)
								} else if (modalState === GCM) {
									if (showName) {
										setShowAlert(true)
									} else {
										setVisible(!visible)
									}
								} else if (modalState === OTP) {
									setShowAlert(true)
								} else {
									setVisible(!visible)
								}
							}}
						/>
					)
				}
			>
				{!showAlert ? (
					<Box
						gap={0}
						css={{
							height: '100vh',
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
							'@lg': {
								height: '100%',
								flexDirection: 'row',
							},
						}}
					>
						{/* for desktop */}
						<Box
							css={{
								height: '40vh',
								width: '100%',
								flex: 1,
								backgroundColor: '$custom31',
								display: modalState === OTP ? 'none' : 'flex',
								'@lg': {
									flex: '0 0 296px',
									height: '100%',
									borderTopLeftRadius: '$3',
									borderBottomLeftRadius: '$3',
									display: 'flex',
								},
								'@smMax': {
									display: 'none',
								},
							}}
						>
							<StyledImage
								src={getImgIXUrl(
									'https://oceanjar-new.s3.ap-south-1.amazonaws.com/images/web_app/gcm/v1/gcm_desk.png',
								)}
								width="100%"
								css={{
									'@lg': {
										borderTopLeftRadius: '$2',
										borderBottomLeftRadius: '$2',
									},
								}}
							/>
						</Box>
						{/* for mobile */}
						{(modalState === GCM || modalState === CHOOSE_LANGUAGE) && (
							<Box
								css={{
									width: '100%',
									height: '122px',
									marginBottom: '40px',
									'@lg': {
										display: 'none',
									},
								}}
							>
								<StyledImage
									src={getImgIXUrl(
										'https://oceanjar-new.s3.ap-south-1.amazonaws.com/images/web_app/gcm/v1/gcm_mobile.png',
									)}
									width="100%"
								/>
							</Box>
						)}
						{rightSideContent}
					</Box>
				) : (
					<>
						{/* for desktop */}
						<Box
							gap={0}
							css={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								'@lg': {
									height: '100%',
									flexDirection: 'row',
								},
								'@smMax': {
									display: 'none',
								},
							}}
						>
							<Box
								css={{
									width: '40%',
									flex: 1,
									display: modalState === OTP ? 'none' : 'flex',
									backgroundImage:
										'url(https://oceanjar-new.s3.ap-south-1.amazonaws.com/gcm-alert-web.png)',
									backgroundRepeat: 'no-repeat',
									backgroundSize: 'cover',
									borderBottomLeftRadius: '$2',
									'@lg': {
										width: '50%',
										height: '100%',
										borderTopLeftRadius: '$3',
										borderBottomLeftRadius: '$3',
										display: 'flex',
									},
								}}
							/>
							<Column
								justify="center"
								align="center"
								gap="3"
								css={{
									width: '60%',
									paddingLeft: '24px',
									paddingRight: '24px',
								}}
							>
								<Text
									css={{
										fontSize: '$6',
										color: '$grey12',
										fontWeight: '$bold',
										lineHeight: '34px',
										letterSpacing: '0.2px',
									}}
								>
									Sure you want to exit?
								</Text>
								<Text
									css={{
										fontSize: '15px',
										color: '$grey12',
										lineHeight: '22px',
										letterSpacing: '0.2px',
										textAlign: 'center',
									}}
								>
									On choosing yes, you will lose the progress you made so far
									and be redirected to the homepage.
								</Text>
								<Row
									justify="between"
									gap="3"
									css={{
										paddingTop: '24px',
									}}
								>
									<Button
										css={{
											width: '170px',
											height: '53px',
											backgroundColor: '$custom27',
											fontSize: '15px',
											color: '$grey12',
											fontWeight: 600,
											borderRadius: '$4',
											border: 'none',
											boxShadow: 'none',
										}}
										onClick={() => {
											setShowAlert(!showAlert)
											setVisible(!visible)
										}}
									>
										Yes
									</Button>
									<Button
										css={{
											width: '170px',
											height: '53px',
											backgroundColor: '$custom32',
											fontSize: '15px',
											fontWeight: 600,
											borderRadius: '$4',
										}}
										onClick={() => {
											setShowAlert(!showAlert)
										}}
									>
										No
									</Button>
								</Row>
							</Column>
						</Box>
						{/* for mobile */}
						<Column
							justify="center"
							align="center"
							css={{
								width: '100%',
								height: '100%',
								backgroundImage:
									'url(https://oceanjar-new.s3.ap-south-1.amazonaws.com/gcm-alert-mobile-bg.png)',
								backgroundSize: 'cover',
								paddingLeft: '20px',
								paddingRight: '20px',
								'@lg': {
									display: 'none',
								},
							}}
						>
							<Column
								css={{
									width: '100%',
									height: '247px',
									borderRadius: '12px',
									backgroundColor: '$custom42',
									position: 'relative',
									paddingLeft: '$4',
									paddingRight: '$4',
								}}
							>
								<Column
									css={{
										position: 'absolute',
										transform: 'translate(0, -50%)',
										left: 'calc(50% - 46px)',
										backgroundImage:
											'url(https://oceanjar-new.s3.ap-south-1.amazonaws.com/gcm-alert-mobile.png)',
										backgroundSize: 'cover',
										width: '91px',
										height: '91px',
									}}
								/>
								<Text
									css={{
										marginTop: '64px',
										fontSize: '18px',
										fontWeight: '$bold',
										color: '$white',
										lineHeight: '34px',
										letterSpacing: '0.2px',
										textAlign: 'center',
									}}
								>
									Sure you want to exit?
								</Text>
								<Text
									css={{
										paddingLeft: '$2',
										paddingRight: '$2',
										fontSize: '13px',
										color: '$white',
										lineHeight: '22px',
										letterSpacing: '0.2px',
										paddingTop: '$1',
										textAlign: 'center',
									}}
								>
									On choosing yes, you will lose the progress you made so far
									and be redirected to the homepage.
								</Text>

								<Row
									gap="4"
									justify="center"
									css={{
										paddingTop: '32px',
									}}
								>
									<Button
										css={{
											width: '100px',
											height: '44px',
											backgroundColor: '$custom44',
											fontSize: '14px',
											color: '$white',
											fontWeight: '$bold',
											borderRadius: '$2',
											border: 'none',
											boxShadow: 'none',
										}}
										onClick={() => {
											setShowAlert(!showAlert)
											setVisible(!visible)
										}}
									>
										Yes
									</Button>
									<Button
										css={{
											width: '100px',
											height: '44px',
											backgroundColor: '$custom32',
											fontSize: '14px',
											color: '$white',
											fontWeight: '$bold',
											borderRadius: '$2',
											border: 'none',
											boxShadow: 'none',
										}}
										onClick={() => {
											setShowAlert(!showAlert)
										}}
									>
										No
									</Button>
								</Row>
							</Column>
						</Column>
					</>
				)}
			</Dialog.Content>
		</Dialog.Root>
	)
}

export default GcmModal
